// REST API client - PRD_SystemDesign 3.2
import { useAuthStore } from '@/state/authStore';
import { translateApiError, translateError } from '@/network/errorMessages';

const BASE = import.meta.env.VITE_API_BASE || 'http://localhost:8080';

export class ApiError extends Error {
  public readonly rawMessage: string;
  constructor(public status: number, public payload: unknown, message: string) {
    super(translateApiError({ status, payload, message }));
    this.rawMessage = message;
  }
}

export { translateApiError, translateError };

async function request<T>(method: string, path: string, body?: unknown, opts: { auth?: boolean } = { auth: true }): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts.auth !== false) {
    const tok = useAuthStore.getState().accessToken;
    if (tok) headers.Authorization = `Bearer ${tok}`;
  }
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const json = text ? safeJSON(text) : undefined;
  if (!res.ok) {
    if (res.status === 401 && opts.auth !== false) {
      const refreshed = await tryRefresh();
      if (refreshed) return request<T>(method, path, body, opts);
    }
    throw new ApiError(res.status, json, (json as { error?: string })?.error || res.statusText);
  }
  return json as T;
}

function safeJSON(s: string): unknown {
  try { return JSON.parse(s); } catch { return s; }
}

let refreshing: Promise<boolean> | null = null;
async function tryRefresh(): Promise<boolean> {
  if (refreshing) return refreshing;
  const { refreshToken, setTokens, clear } = useAuthStore.getState();
  if (!refreshToken) return false;
  refreshing = (async () => {
    try {
      const r = await fetch(`${BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!r.ok) { clear(); return false; }
      const j = await r.json();
      setTokens(j.accessToken, j.refreshToken ?? refreshToken);
      return true;
    } catch { clear(); return false; }
    finally { refreshing = null; }
  })();
  return refreshing;
}

export const api = {
  get:  <T = unknown>(p: string, o?: { auth?: boolean }) => request<T>('GET', p, undefined, o),
  post: <T = unknown>(p: string, b?: unknown, o?: { auth?: boolean }) => request<T>('POST', p, b, o),
  put:  <T = unknown>(p: string, b?: unknown, o?: { auth?: boolean }) => request<T>('PUT', p, b, o),
  del:  <T = unknown>(p: string, o?: { auth?: boolean }) => request<T>('DELETE', p, undefined, o),
};

// Endpoint helpers (theo backend/README.md)
export const Auth = {
  register: (b: { username: string; email: string; password: string }) =>
    api.post<{ accessToken: string; refreshToken: string }>('/api/auth/register', b, { auth: false }),
  login: (b: { username: string; password: string }) =>
    api.post<{ accessToken: string; refreshToken: string }>('/api/auth/login', b, { auth: false }),
  logout: () => api.post('/api/auth/logout'),
};

export type CharacterDTO = {
  id: number;
  name: string;
  class: 'warrior' | 'wizard' | 'archer';
  gender?: number;
  level: number;
  experience: number;
  resetCount: number;
  mapId: number;
  posX: number;
  posY: number;
  hp: number;
  mp: number;
  str: number;
  agi: number;
  vit: number;
  ene: number;
  statPoints: number;
  skillPoints: number;
  zen?: number;
  diamond?: number;
  guildId?: number | null;
};

export const Character = {
  list: () => api.get<{ characters: CharacterDTO[] }>('/api/character/list'),
  create: (b: { name: string; class: CharacterDTO['class'] }) =>
    api.post<CharacterDTO>('/api/character/create', b),
  remove: (id: number) => api.del(`/api/character/${id}`),
  allocateStat: (charId: number, b: { stat: 'str' | 'agi' | 'vit' | 'ene'; amount: number }) =>
    api.post<{ ok: boolean; str: number; agi: number; vit: number; ene: number; statPoints: number }>(
      `/api/character/${charId}/allocate-stat`, b),
  buffs: (charId: number) => api.get<{ buffs: BuffDTO[] }>(`/api/character/${charId}/buffs`),
};

export type BuffDTO = {
  id: number;
  kind: string;
  multiplier: number;
  expiresAt: string;
  remainingS: number;
  sourceTemplate?: number | null;
};

export type PetDTO = {
  id: number;
  templateId: number;
  nickname: string;
  level: number;
  active: boolean;
};

export type PetTemplateDTO = {
  id: number;
  code: string;
  name: string;
  tier: number;
};

export const Pet = {
  templates: () => api.get<{ templates: PetTemplateDTO[] }>('/api/pet/templates'),
  list: (charId: number) => api.get<{ pets: PetDTO[] }>(`/api/pet/${charId}`),
  setActive: (charId: number, b: { petId: number }) =>
    api.post<{ ok: boolean }>(`/api/pet/${charId}/active`, b),
};

export const Inventory = {
  list: (charId: number) => api.get(`/api/inventory/${charId}`),
  templates: () => api.get<{ templates: ItemTemplateDTO[] }>('/api/inventory/templates'),
  equip: (charId: number, b: { itemId: number; slot: string }) => api.post(`/api/inventory/${charId}/equip`, b),
  unequip: (charId: number, b: { itemId?: number; slot?: string }) => api.post(`/api/inventory/${charId}/unequip`, b),
  sell: (charId: number, b: { itemId: number; quantity?: number }) =>
    api.post<{ ok: boolean; itemId: number; templateId: number; quantity: number; unitPrice: number; totalPrice: number; zen: number }>(
      `/api/inventory/${charId}/sell`, b),
};

export type StorageItemDTO = {
  id: number;
  templateId: number;
  slotIndex: number | null;
  quantity: number;
  upgradeLevel: number;
  bound: boolean;
};

export const Storage = {
  list: (charId: number) => api.get<{ items: StorageItemDTO[]; capacity: number }>(`/api/storage/${charId}`),
  deposit: (charId: number, b: { itemId: number; quantity?: number; slotIndex?: number }) =>
    api.post<{ ok: boolean; merged?: boolean; quantity: number; slotIndex?: number }>(`/api/storage/${charId}/deposit`, b),
  withdraw: (charId: number, b: { itemId: number; quantity?: number }) =>
    api.post<{ ok: boolean; merged?: boolean; quantity: number }>(`/api/storage/${charId}/withdraw`, b),
};

export type ItemTemplateDTO = {
  id: number;
  code: string;
  name: string;
  kind: string;
  levelReq: number;
  baseMinDmg: number;
  baseMaxDmg: number;
  baseDefense: number;
  isStackable: boolean;
  isJewel: boolean;
};

export const Skill = {
  templates: () => api.get('/api/skill/templates'),
  list: (charId: number) => api.get(`/api/skill/${charId}`),
  learn: (charId: number, b: { skillId: number }) => api.post(`/api/skill/${charId}/learn`, b),
  levelUp: (charId: number, b: { skillId: number }) => api.post(`/api/skill/${charId}/level-up`, b),
};

export const Quest = {
  templates: () => api.get('/api/quest/templates'),
  list: (charId: number) => api.get(`/api/quest/${charId}`),
  accept: (charId: number, b: { questId: number }) => api.post(`/api/quest/${charId}/accept`, b),
  claim: (charId: number, b: { questId: number }) => api.post(`/api/quest/${charId}/claim`, b),
  talk: (charId: number, b: { npcId: string }) => api.post(`/api/quest/${charId}/talk`, b),
};

export const Mail = {
  list: (charId: number) => api.get(`/api/mail/${charId}`),
  claim: (charId: number, b: { mailId: number }) => api.post(`/api/mail/${charId}/claim`, b),
};

export const Shop = {
  products: () => api.get('/api/shop/products'),
  buy: (b: { characterId: number; productId: number; quantity: number; idemKey: string; currency: 'diamond' | 'zen' }) =>
    api.post('/api/shop/buy', {
      characterId: b.characterId,
      productId: b.productId,
      quantity: b.quantity,
      idempotencyKey: b.idemKey,
      currency: b.currency,
    }),
};

export const Guild = {
  detail: (id: number) => api.get(`/api/guild/${id}`),
  create: (b: { name: string; characterId: number }) => api.post<{ id: number }>('/api/guild', b),
};

export type FriendDTO = { characterId: number; name: string; level: number; class: string };
export type FriendRequestDTO = { id: number; otherId: number; otherName: string; outgoing: boolean };
export type FriendListResp = {
  friends: FriendDTO[];
  requests: { incoming: FriendRequestDTO[]; outgoing: FriendRequestDTO[] };
};

export const Friend = {
  list: (charId: number) => api.get<FriendListResp>(`/api/friend/${charId}`),
  request: (charId: number, b: { targetName: string }) =>
    api.post<{ ok: boolean; accepted: boolean }>(`/api/friend/${charId}/request`, b),
  accept: (charId: number, b: { requestId: number }) => api.post(`/api/friend/${charId}/accept`, b),
  reject: (charId: number, b: { requestId: number }) => api.post(`/api/friend/${charId}/reject`, b),
  cancel: (charId: number, b: { requestId: number }) => api.post(`/api/friend/${charId}/cancel`, b),
  remove: (charId: number, friendId: number) => api.del(`/api/friend/${charId}/${friendId}`),
};

export type TradeItemDTO = {
  itemId: number;
  templateId: number;
  quantity: number;
  upgradeLevel: number;
};
export type TradeSideDTO = {
  characterId: number;
  name: string;
  items: TradeItemDTO[];
  zen: number;
  locked: boolean;
  confirmed: boolean;
};
export type TradeSessionDTO =
  | { id: number; status: number; me: TradeSideDTO; other: TradeSideDTO }
  | { id: 0 };

export const Trade = {
  current: (charId: number) => api.get<TradeSessionDTO>(`/api/trade/${charId}`),
  request: (charId: number, b: { targetName: string }) =>
    api.post<{ ok: boolean; accepted: boolean; tradeId?: number }>(`/api/trade/${charId}/request`, b),
  accept: (charId: number, b: { requestId: number }) =>
    api.post<{ ok: boolean; tradeId: number }>(`/api/trade/${charId}/accept`, b),
  reject: (charId: number, b: { requestId: number }) =>
    api.post(`/api/trade/${charId}/reject`, b),
  cancelRequest: (charId: number, b: { requestId: number }) =>
    api.post(`/api/trade/${charId}/cancel-request`, b),
  add: (charId: number, b: { itemId: number; quantity?: number }) =>
    api.post(`/api/trade/${charId}/add`, b),
  remove: (charId: number, b: { itemId: number }) =>
    api.post(`/api/trade/${charId}/remove`, b),
  zen: (charId: number, b: { amount: number }) =>
    api.post(`/api/trade/${charId}/zen`, b),
  lock: (charId: number) => api.post(`/api/trade/${charId}/lock`),
  unlock: (charId: number) => api.post(`/api/trade/${charId}/unlock`),
  confirm: (charId: number) =>
    api.post<{ ok: boolean; completed: boolean }>(`/api/trade/${charId}/confirm`),
  cancel: (charId: number) => api.post(`/api/trade/${charId}/cancel`),
};

export type PartyMemberDTO = { characterId: number; name: string; level: number; class: string };
export type PartyDetail = { id: number; leaderId: number; members: PartyMemberDTO[] };

export const Party = {
  ofCharacter: (charId: number) => api.get<PartyDetail | { id: 0 }>(`/api/party/of/${charId}`),
  detail: (partyId: number) => api.get<PartyDetail>(`/api/party/${partyId}`),
  create: (b: { leaderId: number }) => api.post<{ id: number }>('/api/party', b),
  invite: (partyId: number, b: { byCharacterId: number; targetName?: string; targetCharacterId?: number }) =>
    api.post(`/api/party/${partyId}/invite`, b),
  leave: (partyId: number, b: { characterId: number }) => api.post(`/api/party/${partyId}/leave`, b),
  kick: (partyId: number, b: { byCharacterId: number; targetCharacterId: number }) =>
    api.post(`/api/party/${partyId}/kick`, b),
};

export const Chat = {
  send: (b: { channel: 'world' | 'guild' | 'party'; characterId: number; body: string }) =>
    api.post('/api/chat/send', b),
  history: () => api.get('/api/chat/history/world'),
};

export type AdminCharacter = {
  id: number;
  accountId: number;
  username: string;
  name: string;
  class: string;
  level: number;
  zen: number;
  diamond: number;
};

export type AdminProduct = {
  id: number;
  code: string;
  name: string;
  itemTemplateId: number | null;
  quantity: number;
  currency: 'diamond' | 'zen';
  price: number;
};

export const Admin = {
  me: () => api.get<{ ok: boolean; isAdmin: boolean }>('/api/admin/me'),
  characters: (q = '') =>
    api.get<{ characters: AdminCharacter[] }>(`/api/admin/characters${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  products: () => api.get<{ products: AdminProduct[] }>('/api/admin/products'),
  giveDiamond: (b: { accountId: number; amount: number }) =>
    api.post<{ ok: boolean; diamond: number }>('/api/admin/give-diamond', b),
  giveZen: (b: { characterId: number; amount: number }) =>
    api.post<{ ok: boolean; zen: number }>('/api/admin/give-zen', b),
  giveItem: (b: { characterId: number; productId: number; quantity: number }) =>
    api.post<{ ok: boolean; itemTemplateId: number; quantity: number }>('/api/admin/give-item', b),
  setWeather: (b: { mapId: number; weather: 'clear' | 'rain' | 'storm' | 'fog' | 'snow'; durationSec?: number }) =>
    api.post<{ ok: boolean }>('/api/admin/set-weather', b),
};
